import { functions } from "config/firebase.config";
import { httpsCallable } from "firebase/functions";
import { MonthMap } from "./types";

export const getPayPeriod = (date) => {
    const d = new Date(date);
    let start, end;
    if (d.getDate() >= 15) {
        start = new Date(d.getFullYear(), d.getMonth(), 15);
        end = new Date(d.getFullYear(), d.getMonth() + 1, 14);
    } else {
        start = new Date(d.getFullYear(), d.getMonth() - 1, 15);
        end = new Date(d.getFullYear(), d.getMonth(), 14);
    }
    return { start, end, id: `${start.toISOString().split('T')[0]}_${end.toISOString().split('T')[0]}` };
};

export const changePayPeriod = (currentPeriodStart, offset) => {
    const newStartDate = new Date(currentPeriodStart);
    newStartDate.setMonth(newStartDate.getMonth() + offset);
    return getPayPeriod(newStartDate).start;
};

export const formatDate = (date) => {
    const d = new Date(date);
    return d.toLocaleDateString('en-CA');
};

export const sendWelcomeEmail = async (email, name, id) => {
    const callsendWelcome = httpsCallable(functions, 'sendWelcomeEmail');
    try {
        const response = await callsendWelcome({ email: email, name: name, id: id });
        return response;
    } catch (error) {
        console.error("Email sending error:", error);
    }
}

export const sendPasswordResetEmail = async (email) => {
    const callsendResetPassword = httpsCallable(functions, 'sendResetPasswordEmail');
    try {
        await callsendResetPassword({ email: email });
    } catch (error) {
        console.error("Email sending error:", error);
    }
}

export const sendLeaveRequestEmail = async (email, name, managerName, leaveType, startDate, endDate, totalDays, comments) => {
    const callsendLeaveRequestEmail = httpsCallable(functions, 'sendLeaveRequestEmail');
    try {
        await callsendLeaveRequestEmail({
            email: email,
            name: name,
            managerName: managerName,
            leaveType: leaveType,
            startDate: formatDate(startDate),
            endDate: formatDate(endDate),
            totalDays: totalDays,
            comments: comments || ''
        });
    } catch (error) {
        console.error("Email sending error:", error);
    }
}

export const sendLeaveRequestEmailByManager = async (email, name, managerName, leaveType, startDate, endDate, totalDays, comments) => {
    const callsendLeaveRequestEmailByManager = httpsCallable(functions, 'sendLeaveRequestEmailByManager');
    try {
        await callsendLeaveRequestEmailByManager({
            email: email,
            name: name,
            managerName: managerName,
            leaveType: leaveType,
            startDate: formatDate(startDate),
            endDate: formatDate(endDate),
            totalDays: totalDays,
            comments: comments || ''
        });
    } catch (error) {
        console.error("Email sending error:", error);
    }
}

export const sendLeaveApprovedEmail = async (email, name, managerName, leaveType, startDate, endDate) => {
    const callsendLeaveApprovedEmail = httpsCallable(functions, 'sendLeaveApprovedEmail');
    try {
        await callsendLeaveApprovedEmail({
            email: email,
            employeeName: name,
            managerName: managerName,
            leaveType: leaveType,
            startDate: formatDate(startDate),
            endDate: formatDate(endDate),
        });
    } catch (error) {
        console.error("Email sending error:", error);
    }
}

export const deleteUserFromAuth = async (uid) => {
    const callDeleteUser = httpsCallable(functions, 'deleteUserFromAuth');
    try {
        await callDeleteUser({ uid: uid });
    } catch (error) {
        console.error("Error deleting user from Auth:", error);
    }
}

export const getNSWHolidays = async () => {
    const callGetNSWHolidays = httpsCallable(functions, "getNSWHolidays");
    try {
        const result = await callGetNSWHolidays();

        const rawData = result?.data?.holidays || [];

        const holidays = rawData.map((entry) => {
            const { date, year, holiday } = entry;

            const cleanDateStr = date.replace(/\u00A0/g, " ").trim();

            const parts = cleanDateStr.split(" ");

            if (parts.length < 3 || cleanDateStr === "Not applicable") {
                return {
                    holiday,
                    year,
                    date,
                    formattedDate: "Not applicable",
                };
            }

            const day = parts[1].padStart(2, "0");
            const monthName = parts[2];
            const month = MonthMap[monthName] || "00";

            const formattedDate = `${year}-${month}-${day}`;

            return {
                holiday,
                year,
                date,
                formattedDate,
            };
        })
        .filter((entry) => !entry.holiday.toLowerCase().includes("bank holiday"));

        return holidays;
    } catch (error) {
        console.error("Error fetching NSW holidays:", error);
        return [];
    }
};

/**
 * Calculates the number of working days between two dates, excluding weekends and public holidays.
 * @param {string|Date} startDate - The start date (inclusive)
 * @param {string|Date} endDate - The end date (inclusive)
 * @param {Array} publicHolidays - Optional array of public holidays. Each holiday should have a 'formattedDate' property in 'YYYY-MM-DD' format.
 * @returns {number} The number of working days (excluding weekends and public holidays)
 */
export const calculateWorkingDays = (startDate, endDate, publicHolidays = []) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    // Normalize dates to start of day to avoid timezone issues
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    
    // Create a Set of public holiday dates for fast lookup
    const holidayDates = new Set(
        publicHolidays
            .filter(h => h.formattedDate && h.formattedDate !== "Not applicable")
            .map(h => {
                // Normalize holiday date to YYYY-MM-DD format for comparison
                const holidayDate = new Date(h.formattedDate);
                holidayDate.setHours(0, 0, 0, 0);
                return holidayDate.getTime();
            })
    );
    
    let workingDays = 0;
    const currentDate = new Date(start);
    
    // Iterate through each day from start to end (inclusive)
    while (currentDate <= end) {
        const dayOfWeek = currentDate.getDay();
        const dateTime = currentDate.getTime();
        
        // Check if it's a weekday (Monday=1 to Friday=5) and not a public holiday
        if (dayOfWeek !== 0 && dayOfWeek !== 6 && !holidayDates.has(dateTime)) {
            workingDays++;
        }
        
        // Move to next day
        currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return workingDays;
};